/**
 * Unified Action Panel Component
 * Consistent action panel for all management pages
 */

import React from 'react';
import './UnifiedActionPanel.css';

const UnifiedActionPanel = ({
    selectedCount,
    totalCount,
    selectedItemsLabel,
    onSelectAll,
    onClearSelection,
    selectedAction,
    onActionChange,
    onExecuteAction,
    isProcessing,
    actionOptions = [],
    children
}) => {
    // Helper function to handle pluralization dynamically
    const pluralize = (count, singular, plural = null) => {
        if (count === 1) return singular;
        return plural || `${singular}s`;
    };

    // Robust selection summary with proper pluralization and edge case handling
    const getSelectionSummary = () => {
        // Handle edge cases
        if (selectedCount === null || selectedCount === undefined || selectedCount < 0) {
            return null;
        }

        if (selectedCount === 0) {
            return null;
        }

        // Validate selectedItemsLabel
        if (!selectedItemsLabel || typeof selectedItemsLabel !== 'string') {
            return `${selectedCount} item${selectedCount === 1 ? '' : 's'}`;
        }

        // Handle singular case
        if (selectedCount === 1) {
            return `1 ${selectedItemsLabel}`;
        }

        // Handle plural case with dynamic pluralization
        const pluralizedLabel = pluralize(selectedCount, selectedItemsLabel);
        return `${selectedCount} ${pluralizedLabel}`;
    };

    return (
        <div className="unified-action-panel">
            <div className="unified-action-panel-content">
                <div className="unified-action-section">
                    <h4 className="unified-action-section-title">
                        {selectedCount === 0 ? `👤 Single ${selectedItemsLabel || 'Item'} Actions` :
                            selectedCount === 1 ? `👤 Single ${selectedItemsLabel || 'Item'} Actions - ${getSelectionSummary()}` :
                                `⚡ Bulk Actions (${getSelectionSummary()})`}
                    </h4>

                    <div className="unified-action-controls-grid">
                        {/* Selection Controls */}
                        <div className="unified-selection-controls-group">
                            <div className="unified-select-all-container">
                                <label className="unified-checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="unified-checkbox"
                                        checked={selectedCount === totalCount && totalCount > 0}
                                        onChange={(e) => onSelectAll(e.target.checked)}
                                    />
                                    <span className="unified-checkbox-custom"></span>
                                    <span className="unified-checkbox-text">
                                        <span className="unified-checkbox-icon">☑️</span>
                                        Select All {selectedItemsLabel}
                                    </span>
                                </label>
                            </div>

                            {selectedCount > 0 && (
                                <button
                                    className="unified-clear-selection-btn"
                                    onClick={onClearSelection}
                                >
                                    <span className="unified-btn-icon">❌</span>
                                    <span className="unified-btn-text">Clear Selection</span>
                                </button>
                            )}
                        </div>

                        {/* Action Controls */}
                        <div className="unified-action-controls-group">
                            <div className="unified-action-selector">
                                <div className="unified-action-select-wrapper">
                                    <select
                                        className="unified-action-select"
                                        value={selectedAction}
                                        onChange={(e) => onActionChange(e.target.value)}
                                        disabled={selectedCount === 0}
                                    >
                                        <option value="">🎯 Choose Action</option>
                                        {actionOptions.map((option, index) => (
                                            <option
                                                key={index}
                                                value={option.value}
                                                disabled={option.disabled}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="unified-select-arrow">▼</div>
                                </div>
                            </div>

                            <button
                                className="unified-execute-btn"
                                onClick={onExecuteAction}
                                disabled={!selectedAction || selectedCount === 0 || isProcessing}
                            >
                                <span className="unified-btn-icon">
                                    {isProcessing ? '⏳' : '🚀'}
                                </span>
                                <span className="unified-btn-text">
                                    {isProcessing ? 'Processing...' : `Execute on ${getSelectionSummary() || 'items'}`}
                                </span>
                                {selectedCount > 1 && (
                                    <span className="unified-btn-badge">{selectedCount}</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Action Content */}
            {children && (
                <div className="unified-additional-actions">
                    {children}
                </div>
            )}
        </div>
    );
};

export default UnifiedActionPanel;
